import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from "react-redux";
import { Drawer } from "@material-tailwind/react";
import { BeatLoader } from "react-spinners";
import { IoMdClose, IoIosArrowForward } from "react-icons/io";
import { getReportReasons } from "../../redux/ApiSlice/ReportSlice";

export default function CommentReport({ handleReportComment, setReportPost }) {

    const dispatch = useDispatch();
    const { reportReasons = [], reportReasonsLoading } = useSelector((state) => state.Report);

    const [reportStep, setReportStep] = useState("reason");
    const [selectedReason, setSelectedReason] = useState(null);
    const [message, setMessage] = useState("");
    const [reportLoading, setReportLoading] = useState(false);
    const [reportError, setReportError] = useState("");

    useEffect(() => {
        dispatch(getReportReasons());
    }, [dispatch]);

    const handleReasonSelect = (selectedReasonOption) => {
        setSelectedReason(selectedReasonOption);
        setMessage("");
        setReportError("");
        setReportStep("detail");
    };

    const handleBackToReasons = () => {
        setReportStep("reason");
        setMessage("");
        setReportError("");
    };

    const handleSubmitReport = async () => {
        if (!selectedReason) return;

        setReportError("");
        setReportLoading(true);
        try {
            await handleReportComment(selectedReason.code, message);
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.payload?.message || error?.message || "You have already reported this comment.";
            setReportError(errorMessage);
        } finally {
            setReportLoading(false);
        }
    };

    const reportDrawerSize = reportStep === "detail" ? 420 : 480;

    return (
        <Drawer
            size={reportDrawerSize}
            placement="bottom"
            open={true}
            onClose={() => setReportPost()}
            className="absolute w-full rounded-t-2xl bg-white dark:bg-gray-900 max-h-[90vh] shadow-2xl"
            style={{ zIndex: 3 }}
        >
            <div className="relative flex h-full max-h-[80vh] flex-col">
                <div className="relative flex items-center justify-center px-4 pt-3 pb-2">
                    {reportStep === "detail" && (
                        <IoIosArrowForward
                            onClick={handleBackToReasons}
                            className="absolute left-4 top-4 cursor-pointer rotate-180 text-[20px] text-gray-800 dark:text-gray-100"
                        />
                    )}
                    {reportStep !== "detail" && (
                        <IoMdClose
                            onClick={() => setReportPost()}
                            className="absolute right-4 top-3 cursor-pointer text-[20px] text-gray-800 dark:text-gray-100"
                        />
                    )}
                </div>

                {reportStep === "reason" && (
                    <div className="flex h-full flex-col overflow-y-auto pb-6">
                        <div className="px-4 pt-1 pb-4 text-center space-y-1">
                            <h2 className="text-[16px] font-semibold text-black dark:text-white">Report comment</h2>
                            <p className="text-[11px] text-gray-600 dark:text-gray-300">
                                Reports are confidential. The person who commented won't know you reported it.
                            </p>
                        </div>

                        <div className="border-t border-b border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800">
                            {reportReasonsLoading ? (
                                <div className="flex justify-center items-center py-8">
                                    <BeatLoader size={8} color={"#6b7280"} />
                                </div>
                            ) : (
                                reportReasons.map((reasonOption) => (
                                    <button
                                        key={reasonOption.code}
                                        type="button"
                                        onClick={() => handleReasonSelect(reasonOption)}
                                        className="flex w-full items-center justify-between px-5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                                    >
                                        <span className="text-[12px] font-medium text-gray-900 dark:text-gray-100">{reasonOption.label}</span>
                                        <IoIosArrowForward className="text-[16px] text-gray-400" />
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {reportStep === "detail" && (
                    <div className="flex h-full flex-col overflow-y-auto pb-4">
                        <div className="px-4 pt-2 pb-3 text-center space-y-1">
                            <h3 className="text-[14px] font-semibold text-black dark:text-white">{selectedReason?.label}</h3>
                            <p className="text-[12px] text-gray-600 dark:text-gray-300">
                                Tell us more so we can review this comment.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 px-4">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3">
                                <textarea
                                    value={message}
                                    maxLength={200}
                                    placeholder="Share anything that might help us understand the issue (optional)"
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="h-[90px] w-full resize-none bg-transparent text-[13px] text-gray-900 placeholder-gray-500 outline-none dark:text-gray-100 dark:placeholder-gray-400"
                                />
                                <div className="mt-1 flex items-center justify-end text-[11px] text-gray-500 dark:text-gray-400">
                                    <span>{message.length}/200</span>
                                </div>
                            </div>
                        </div>

                        {reportError && (
                            <div className="px-4 pt-3">
                                <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-[12px] font-medium text-red-600 dark:bg-red-900/30 dark:text-red-400">
                                    {reportError}
                                </p>
                            </div>
                        )}

                        <div className="mt-auto flex gap-3 px-4 pt-4 pb-3">
                            <button
                                type="button"
                                onClick={() => setReportPost()}
                                className="flex-1 rounded-xl border border-gray-300 bg-white py-2 text-[14px] font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={reportLoading}
                                onClick={handleSubmitReport}
                                className={`flex flex-1 items-center justify-center rounded-xl py-2 text-[14px] font-semibold transition ${reportLoading ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-800 dark:text-gray-500" : "bg-red-600 text-white hover:bg-red-700"}`}
                            >
                                {reportLoading ? <BeatLoader size={10} color={"#fff"} loading={reportLoading} /> : "Submit report"}
                            </button>
                        </div>

                        <p className="px-6 text-center text-[11px] text-gray-500 dark:text-gray-400">
                            Reports are confidential. The person who commented won't know you reported it.
                        </p>
                    </div>
                )}
            </div>
        </Drawer>
    )
}
